import { createTheme } from '@mui/material/styles';

// Bazooka Joe inspired color palette with pink instead of red
export const bazookaColors = {
  pink: '#FF69B4',      // Hot pink (replacing classic Bazooka red)
  blue: '#0066CC',      // Classic comic blue
  yellow: '#FFD700',    // Golden yellow
  black: '#1A1A1A',     // Rich black
  cream: '#FFF8DC',     // Off-white/cream background
  white: '#FFFFFF',
  
  // Risograph-style overlays
  pinkOverlay: 'rgba(255, 105, 180, 0.15)',
  blueOverlay: 'rgba(0, 102, 204, 0.15)',
  yellowOverlay: 'rgba(255, 215, 0, 0.15)',
  
  // UI states
  success: '#2ECC71',
  error: '#E74C3C',
  warning: '#F39C12',
  info: '#3498DB',
  hotNeonPink: '#FF1493',
  electricCyan: '#00CED1',
  neonMagenta: '#FF00FF',
  gold: '#FFD700',
  mediumPurple: '#9370DB',
  springGreen: '#00FF7F',
  lavenderMist: '#E6E6FA',
  indigo: '#4B0082',
  darkMagenta: '#8B008B',
};

// Comic book inspired fonts
const typography = {
  fontFamily: '"Comic Neue", "Comic Sans MS", cursive, sans-serif',
  h1: {
    fontFamily: '"Bungee", cursive',
    fontSize: '4.5rem',
    fontWeight: 400,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    // Chrome effect text
    background: 'linear-gradient(180deg, #FFD700 0%, #FFA500 25%, #FF69B4 50%, #FF1493 75%, #9370DB 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    textShadow: '0 0 20px rgba(255,20,147,0.5), 0 0 40px rgba(255,20,147,0.3)',
    filter: 'drop-shadow(3px 3px 0px #000)',
  },
  h2: {
    fontFamily: '"Righteous", sans-serif',
    fontSize: '3.5rem',
    fontWeight: 400,
    letterSpacing: '0.05em',
    color: '#FF1493',
    textShadow: '3px 3px 0px #00CED1, 6px 6px 0px #FFD700',
  },
  h3: {
    fontFamily: '"Righteous", sans-serif',
    fontSize: '2.5rem',
    fontWeight: 400,
    letterSpacing: '0.03em',
    textShadow: '2px 2px 0px #FF69B4',
  },
  h4: {
    fontFamily: '"Fredoka", sans-serif',
    fontSize: '2rem',
    fontWeight: 500,
  },
  h5: {
    fontFamily: '"Fredoka", sans-serif',
    fontSize: '1.5rem',
    fontWeight: 500,
  },
  h6: {
    fontFamily: '"Fredoka", sans-serif',
    fontSize: '1.25rem',
    fontWeight: 500,
  },
  body1: {
    fontFamily: '"Fredoka", sans-serif',
    fontSize: '1rem',
    fontWeight: 300,
  },
  button: {
    fontFamily: '"Righteous", sans-serif',
    fontSize: '1.2rem',
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
  },
};

// Create the Bazooka Joe theme
export const bazookaTheme = createTheme({
  palette: {
    primary: {
      main: bazookaColors.hotNeonPink,
      light: '#FF69B4',
      dark: '#C71585',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: bazookaColors.electricCyan,
      light: '#40E0D0',
      dark: '#008B8B',
      contrastText: '#000000',
    },
    error: {
      main: bazookaColors.neonMagenta,
    },
    warning: {
      main: bazookaColors.gold,
    },
    info: {
      main: bazookaColors.mediumPurple,
    },
    success: {
      main: bazookaColors.springGreen,
    },
    background: {
      default: bazookaColors.lavenderMist,
      paper: '#FFFFFF', // Changed from gradient - MUI doesn't support gradients in background.paper
    },
    text: {
      primary: bazookaColors.indigo,
      secondary: bazookaColors.darkMagenta,
    },
  },
  typography,
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '25px',
          border: '3px solid transparent',
          backgroundImage: 'linear-gradient(145deg, #FF69B4, #FFD700), linear-gradient(145deg, #00CED1, #9370DB)',
          backgroundOrigin: 'border-box',
          backgroundClip: 'padding-box, border-box',
          boxShadow: '0 4px 15px rgba(255,20,147,0.4), inset 0 2px 4px rgba(255,255,255,0.5)',
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.3s ease',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '-2px',
            left: '-2px',
            right: '-2px',
            bottom: '-2px',
            background: 'linear-gradient(45deg, #FF1493, #00CED1, #FFD700, #9370DB, #FF1493)',
            backgroundSize: '400% 400%',
            borderRadius: '25px',
            opacity: 0,
            zIndex: -1,
            animation: 'rainbow-shift 3s ease infinite',
            transition: 'opacity 0.3s ease',
          },
          '&:hover': {
            transform: 'translateY(-2px) scale(1.05)',
            boxShadow: '0 6px 20px rgba(255,20,147,0.6), inset 0 2px 4px rgba(255,255,255,0.8)',
            '&::before': {
              opacity: 1,
            },
          },
          '&:active': {
            transform: 'translateY(1px) scale(0.98)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          border: '2px solid #FF69B4',
          borderRadius: '20px',
          background: 'linear-gradient(145deg, rgba(255,255,255,0.9), rgba(230,230,250,0.9))',
          boxShadow: '0 8px 32px rgba(147,112,219,0.3), inset 0 2px 4px rgba(255,255,255,0.8)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              radial-gradient(circle, ${bazookaColors.black} 1px, transparent 1px)
            `,
            backgroundSize: '10px 10px',
            opacity: 0.03,
            pointerEvents: 'none',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: bazookaColors.white,
            border: `2px solid ${bazookaColors.black}`,
            borderRadius: '15px',
            '&:hover': {
              borderColor: bazookaColors.pink,
            },
            '&.Mui-focused': {
              borderColor: bazookaColors.pink,
              boxShadow: `3px 3px 0px ${bazookaColors.black}`,
            },
            '& fieldset': {
              border: 'none',
            },
          },
          '& .MuiInputLabel-root': {
            color: bazookaColors.black,
            fontFamily: '"Comic Neue", cursive',
            fontWeight: 700,
            '&.Mui-focused': {
              color: bazookaColors.pink,
            },
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: `3px solid ${bazookaColors.black}`,
          boxShadow: `5px 5px 0px ${bazookaColors.black}`,
          borderRadius: '12px',
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            top: '-8px',
            right: '-8px',
            width: '30px',
            height: '30px',
            background: bazookaColors.yellow,
            borderRadius: '50%',
            border: `3px solid ${bazookaColors.black}`,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          border: `2px solid ${bazookaColors.black}`,
          borderRadius: '20px',
          fontFamily: '"Comic Neue", cursive',
          fontWeight: 700,
          boxShadow: `2px 2px 0px ${bazookaColors.black}`,
        },
        colorPrimary: {
          backgroundColor: bazookaColors.pink,
          color: bazookaColors.white,
        },
        colorSecondary: {
          backgroundColor: bazookaColors.blue,
          color: bazookaColors.white,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: 12,
          borderRadius: 6,
          border: `2px solid ${bazookaColors.black}`,
          backgroundColor: bazookaColors.white,
          boxShadow: `2px 2px 0px ${bazookaColors.black}`,
        },
        bar: {
          borderRadius: 4,
          background: `repeating-linear-gradient(
            -45deg,
            ${bazookaColors.pink},
            ${bazookaColors.pink} 10px,
            ${bazookaColors.yellow} 10px,
            ${bazookaColors.yellow} 20px
          )`,
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          border: `3px solid ${bazookaColors.black}`,
          boxShadow: `3px 3px 0px ${bazookaColors.black}`,
        },
      },
    },
  },
});

// CSS utility classes for comic effects
export const comicEffects = {
  speechBubble: `
    position: relative;
    background: white;
    border: 3px solid ${bazookaColors.black};
    border-radius: 20px;
    padding: 20px;
    box-shadow: 4px 4px 0px ${bazookaColors.black};
    
    &::after {
      content: '';
      position: absolute;
      bottom: -15px;
      left: 30px;
      width: 0;
      height: 0;
      border-left: 15px solid transparent;
      border-right: 15px solid transparent;
      border-top: 15px solid ${bazookaColors.black};
      transform: rotate(-15deg);
    }
    
    &::before {
      content: '';
      position: absolute;
      bottom: -11px;
      left: 32px;
      width: 0;
      height: 0;
      border-left: 13px solid transparent;
      border-right: 13px solid transparent;
      border-top: 13px solid white;
      transform: rotate(-15deg);
      z-index: 1;
    }
  `,
  
  comicPanel: `
    border: 4px solid ${bazookaColors.black};
    background: white;
    position: relative;
    overflow: hidden;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: repeating-linear-gradient(
        45deg,
        transparent,
        transparent 20px,
        ${bazookaColors.blueOverlay} 20px,
        ${bazookaColors.blueOverlay} 21px
      );
      pointer-events: none;
    }
  `,
  
  halftone: `
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-image: radial-gradient(circle, ${bazookaColors.black} 1px, transparent 1px);
      background-size: 4px 4px;
      opacity: 0.1;
      pointer-events: none;
    }
  `,
  
  pow: `
    position: relative;
    background: ${bazookaColors.yellow};
    border: 4px solid ${bazookaColors.black};
    padding: 20px;
    clip-path: polygon(
      10% 0%, 90% 0%, 100% 10%, 100% 90%, 90% 100%,
      10% 100%, 0% 90%, 0% 10%
    );
    transform: rotate(-5deg);
    
    &::before {
      content: 'POW!';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-family: 'Bangers', cursive;
      font-size: 3rem;
      color: ${bazookaColors.black};
      text-shadow: 3px 3px 0px ${bazookaColors.pink};
    }
  `,
};

// Font imports to add to index.html or import in App
export const fontImports = `
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="https://fonts.googleapis.com/css2?family=Bangers&family=Comic+Neue:wght@400;700&display=swap" rel="stylesheet">
`;
